import { useMemo, useState } from 'react';
import { Users, Plus, MoreHorizontal } from 'lucide-react';
import { Toolbar } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import {
  Button,
  Badge,
  Skeleton,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  IconButton,
} from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useProjectTeams,
  useRemoveProjectTeam,
  type ProjectTeam,
} from '@/api/projects';
import { AddTeamDialog } from './components/AddTeamDialog';
import { EditTeamRoleDialog } from './components/EditTeamRoleDialog';
import { ROLE_TONE, ROLE_LABEL, formatDate } from './shared';

interface ProjectTeamsTabProps {
  projectId: string;
  canManage: boolean;
  /** Whether the current user is a project owner (vs an admin who can manage). */
  actorIsOwner: boolean;
}

export function ProjectTeamsTab({ projectId, canManage, actorIsOwner }: ProjectTeamsTabProps) {
  const { show } = useToast();
  const { data: teams = [], isLoading } = useProjectTeams(projectId);
  const ownerCount = useMemo(() => teams.filter((t) => t.role === 'owner').length, [teams]);
  // An owner team can't be removed/demoted by a non-owner (escalation), and the
  // last owner can never be removed/demoted (it would orphan the project).
  const isProtectedOwner = (t: ProjectTeam) =>
    t.role === 'owner' && (!actorIsOwner || ownerCount <= 1);

  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<ProjectTeam | null>(null);
  const [removing, setRemoving] = useState<ProjectTeam | null>(null);

  const remove = useRemoveProjectTeam(projectId, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Team removed from project' });
      setRemoving(null);
    },
    onError: () => show({ tone: 'danger', title: 'Could not remove team' }),
  });

  const existingTeamIds = useMemo(() => teams.map((t) => t.team_id), [teams]);

  const columns = useMemo<ColumnDef<ProjectTeam, unknown>[]>(() => {
    const cols: ColumnDef<ProjectTeam, unknown>[] = [
      {
        id: 'team',
        header: 'Team',
        accessorKey: 'team_name',
        cell: ({ row }) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)', fontWeight: 500 }}>
            <Users size={14} color="var(--vn-fg-muted)" />
            {row.original.team_name ?? row.original.team_id}
          </span>
        ),
      },
      {
        id: 'role',
        header: 'Role',
        enableSorting: false,
        cell: ({ row }) => (
          <Badge tone={ROLE_TONE[row.original.role]} appearance="subtle" size="sm">
            {ROLE_LABEL[row.original.role]}
          </Badge>
        ),
      },
      {
        id: 'added',
        header: 'Added',
        accessorKey: 'created_at',
        cell: ({ row }) => (
          <span style={{ color: 'var(--vn-fg-muted)' }}>{formatDate(row.original.created_at)}</span>
        ),
      },
    ];

    if (canManage) {
      cols.push({
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div style={{ textAlign: 'right' }}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <IconButton variant="ghost" size="sm" aria-label="Team actions">
                  <MoreHorizontal size={16} />
                </IconButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  disabled={!actorIsOwner && row.original.role === 'owner'}
                  onSelect={() => setEditing(row.original)}
                >
                  Change role
                </DropdownMenuItem>
                <DropdownMenuItem
                  tone="danger"
                  disabled={isProtectedOwner(row.original)}
                  onSelect={() => setRemoving(row.original)}
                >
                  Remove from project
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      });
    }

    return cols;
  }, [canManage, actorIsOwner, ownerCount]);

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {canManage && (
        <Toolbar
          end={
            <Button
              variant="secondary"
              size="sm"
              startIcon={<Plus size={15} />}
              onClick={() => setAddOpen(true)}
            >
              Add team
            </Button>
          }
        />
      )}

      {isLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 44 }} />
          ))}
        </div>
      ) : teams.length === 0 ? (
        <EmptyState
          icon={<Users size={28} />}
          title="No teams in this project yet"
          description={
            canManage
              ? 'Add one of your teams to grant it access to this project.'
              : 'No teams have been added to this project.'
          }
        />
      ) : (
        <DataTable columns={columns} data={teams} getRowId={(r) => r.id} />
      )}

      <AddTeamDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        projectId={projectId}
        existingTeamIds={existingTeamIds}
      />
      <EditTeamRoleDialog
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        projectId={projectId}
        team={editing}
        actorIsOwner={actorIsOwner}
        isLastOwner={!!editing && editing.role === 'owner' && ownerCount <= 1}
      />
      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        title="Remove team?"
        description={`“${removing?.team_name ?? 'This team'}” will lose access to this project.`}
        confirmLabel="Remove"
        destructive
        loading={remove.isPending}
        onConfirm={() => removing && remove.mutate(removing.team_id)}
      />
    </div>
  );
}
